"use client";
import { jsx } from "react/jsx-runtime";
import { DatePicker } from "@pcln/horizon";
import { format } from "date-fns/format";
import { useCallback, useEffect, useState } from "react";
import FooterLeftNode from "./FooterLeftNode.js";
import { getPricingData } from "./getPricingData/getPricingData.js";
import { getDisplayMonths, pricePromise, processRecords, shouldCallCalendarApi } from "./helper/helper.js";
const getLowestPrice = (priceMap)=>Math.min(...Object.keys(priceMap).map((k)=>priceMap[k].price));
const DAY_MAP_VARIANT = 'dayHeatMap';
function HorizonFlyDatePicker({ flightDetails, startDate, variantName, getGraphURL, apolloClientInfo, selected, mode = 'range', numberOfMonths = 2, defaultMonth, startMonth, endMonth, title, label, handleCalendarChange, ...restProps }) {
    const DISPLAY_NUMBER_OF_MONTHS = numberOfMonths;
    const [dayMap, setDayMap] = useState({
        variant: DAY_MAP_VARIANT
    });
    const handleCalendarDatesChange = (dates, triggerDate)=>{
        if (!dates) return;
        let displayedFromMonth;
        let displayedToMonth;
        let departDate;
        if ('range' === mode && 'from' in dates && dates.from) {
            displayedFromMonth = format(dates.from, 'yyyyMM');
            displayedToMonth = format(dates?.to || dates.from, 'yyyyMM');
            departDate = format(dates.from, 'yyyyMMdd');
        } else {
            displayedFromMonth = format(dates, 'yyyyMM');
            displayedToMonth = displayedFromMonth;
        }
        const displayMonths = {
            displayedFromMonth,
            displayedToMonth
        };
        getAirPricedCalendarData({
            displayMonths,
            departDate
        });
        handleCalendarChange?.(dates, triggerDate);
    };
    const getAirPricedCalendarData = useCallback(({ displayMonths, departDate })=>{
        if (!shouldCallCalendarApi({
            flightDetails
        })) return;
        getPricingData({
            displayMonths,
            flightDetails,
            pricePromise: pricePromise,
            variantName,
            departDate,
            getGraphURL,
            apolloClientInfo
        }).then((apiRecords)=>{
            if (!apiRecords) return;
            const processedRecords = processRecords({
                apiRecords: apiRecords,
                doubleCalendarPricing: false
            });
            const numberOfProcessedRecords = Object.keys(processedRecords)?.length;
            const lowestPrice = numberOfProcessedRecords > 0 ? getLowestPrice(processedRecords) : void 0;
            setDayMap(()=>({
                    data: processedRecords,
                    variant: DAY_MAP_VARIANT,
                    lowestPrice
                }));
        }).catch(()=>{});
    }, [
        flightDetails,
        variantName,
        getGraphURL,
        apolloClientInfo
    ]);
    const onMonthChange = (month)=>{
        const displayMonths = getDisplayMonths({
            startDate: month,
            numberOfMonths: DISPLAY_NUMBER_OF_MONTHS
        });
        getAirPricedCalendarData({
            displayMonths
        });
    };
    useEffect(()=>{
        const displayMonths = getDisplayMonths({
            startDate: startDate ? new Date(startDate) : new Date(),
            numberOfMonths: DISPLAY_NUMBER_OF_MONTHS
        });
        getAirPricedCalendarData({
            displayMonths
        });
    }, []);
    return /*#__PURE__*/ jsx(DatePicker, {
        ...restProps,
        title: title,
        label: label,
        mode: mode,
        selected: selected,
        defaultMonth: defaultMonth,
        startMonth: startMonth,
        endMonth: endMonth,
        dayMap: dayMap,
        numberOfMonths: numberOfMonths,
        handleCalendarChange: handleCalendarDatesChange,
        onMonthChange: onMonthChange,
        footerLeftNode: /*#__PURE__*/ jsx(FooterLeftNode, {
            lowestPrice: dayMap?.lowestPrice || 0
        })
    });
}
export { HorizonFlyDatePicker };
